import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, AlertCircle, ArrowLeft } from "lucide-react";
import { LLink } from "@/i18n/LLink";
import SubmissionForm from "@/components/forms/SubmissionForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Landings-pagina voor de magic-link. Verifieert het token uit ?token=xxx tegen
 * de backend en toont pas na succesvolle verificatie het (vooraf-ingevulde)
 * bewerkformulier.
 *
 * Referrer-policy op no-referrer voorkomt dat het token via de Referer-header
 * naar externe embeds (Google Maps, e.d.) lekt.
 */
export const AanpassenPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [state, setState] = useState("verifying"); // verifying | ok | error
  const [listing, setListing] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  useEffect(() => {
    // Zet referrer-policy meta zodat het token niet lekt via Referer-header.
    let meta = document.querySelector('meta[name="referrer"]');
    const original = meta?.getAttribute("content") || null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "referrer");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "no-referrer");
    return () => {
      if (original !== null) meta.setAttribute("content", original);
      else meta.remove();
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setState("error");
      setErrorMsg("Geen verificatietoken meegegeven.");
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/submissions/verify-edit-token`, {
          params: { token },
        });
        setListing(res.data.listing);
        setExpiresAt(res.data.expires_at);
        setState("ok");
      } catch (err) {
        setState("error");
        setErrorMsg(
          err?.response?.data?.detail ||
            "Deze link is ongeldig, verlopen of al gebruikt."
        );
      }
    })();
  }, [token]);

  if (state === "verifying") {
    return (
      <div data-testid="aanpassen-verifying" className="ll-container py-24 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white shadow-md mb-4">
          <ShieldCheck className="w-6 h-6 text-[#2E9CD6] animate-pulse" />
        </div>
        <p className="text-[#173A5E] font-[Poppins] font-semibold">Verificatie…</p>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="ll-container py-24">
        <div
          data-testid="aanpassen-invalid"
          className="max-w-xl mx-auto rounded-2xl border border-[#F4B5BA] bg-[#FFF6F7] p-8 text-center"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white shadow-md mb-4">
            <AlertCircle className="w-6 h-6 text-[#CE1B2A]" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#173A5E] mb-2">
            Link niet geldig
          </h1>
          <p className="text-[#3E5871] font-[Inter] mb-6">{errorMsg}</p>
          <LLink to="/voor-ondernemers" className="ll-btn-primary">
            <ArrowLeft className="w-4 h-4" />
            Vraag een nieuwe link aan
          </LLink>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <section className="ll-container py-14 sm:py-20">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8 flex items-start gap-4 p-5 rounded-2xl border border-[#6BC0E8] bg-[#EAF6FC]">
            <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm">
              <ShieldCheck className="w-6 h-6 text-[#2E9CD6]" />
            </div>
            <div>
              <h2 className="font-[Poppins] font-bold text-[#173A5E] text-lg mb-1">
                Geverifieerd — je kunt <span className="text-[#2E9CD6]">{listing?.name}</span> nu aanpassen
              </h2>
              <p className="text-[#3E5871] font-[Inter] text-sm">
                Wijzigingen komen na versturen bij Herman langs voor een korte controle.
                Je e-mailadres is het verificatie-anker en kan hier niet worden gewijzigd.
                {expiresAt && (
                  <> · Deze link vervalt na verzenden of automatisch na 45 minuten.</>
                )}
              </p>
            </div>
          </div>
          <SubmissionForm
            mode="aanvulling"
            lockedListing={listing}
            editToken={token}
            key={listing?.slug}
          />
        </div>
      </section>
    </div>
  );
};

export default AanpassenPage;
